import React from 'react';
import { useIntl } from 'react-intl';
import type { MediaWidgetSource } from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../../../common/types';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { PickerSongBar } from '../../../../../music24/promote/composer/picker/PickerSongBar';
import { PickerUploadBox } from '../../../../../music24/promote/composer/picker/PickerUploadBox';
import { mediaBlockMessages } from '../mediaBlockMessages';
import { MediaBlockPick } from './mediaBlockPick';
import { MediaPickedRow } from './MediaPickedRow';
import { MediaPickerTiles } from './MediaPickerTiles';
import { MediaBlockPicker } from './useMediaBlockPicker';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaSourceSectionProps {
  items: CatalogItem[];
  song: string | undefined;
  onSong: (uuid: string | undefined) => void;
  picker: MediaBlockPicker;
  pick: MediaBlockPick | null;
  source: MediaWidgetSource | null;
  /** True while the tile list is open; after a pick it folds into one row (design a4). */
  isChoosing: boolean;
  onChoose: () => void;
  onPick: (pick: MediaBlockPick) => void;
  onOpenStudio: () => void;
}

/**
 * The File section of the rail: the song filter ("All songs" or one song; the default is the song
 * of the page), the cover and the files of the song, and Upload (design a2, a5, a6).
 */
const MediaSourceSectionComponent: React.FC<MediaSourceSectionProps> = ({
  items,
  song,
  onSong,
  picker,
  pick,
  source,
  isChoosing,
  onChoose,
  onPick,
  onOpenStudio,
}) => {
  const intl = useIntl();
  const { upload } = picker;

  return (
    <section className={styles.section} data-testid="media-source">
      <h6 className={styles.sectionTitle}>{intl.formatMessage(mediaBlockMessages.file)}</h6>
      {pick !== null && !isChoosing ? (
        <MediaPickedRow pick={pick} onChange={onChoose} />
      ) : (
        <>
          <PickerSongBar
            items={items}
            song={song}
            onSong={onSong}
            filter={picker.filter}
            onFilter={picker.setFilter}
            noSongLabel={intl.formatMessage(mediaBlockMessages.allSongs)}
          />
          <MediaPickerTiles picker={picker} selected={source} onPick={onPick} onOpenStudio={onOpenStudio} />
          <PickerUploadBox
            state={upload.state}
            onUpload={upload.upload}
            onCancel={upload.cancel}
            onRetry={upload.retry}
          />
          {pick === null && <span className={styles.note}>{intl.formatMessage(mediaBlockMessages.noFile)}</span>}
        </>
      )}
    </section>
  );
};

export const MediaSourceSection = TypedMemo(MediaSourceSectionComponent);
